import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../api/api-context';
import type { GameRepertoireMatch } from '../api/types';
import { pickReviewTarget } from '../state/game-review';
import { GameLinkPrompt } from './GameLinkPrompt';
import { GameReview } from './GameReview';

const RECENT_GAMES = 50;

/**
 * /games — opens straight onto a review. The server matches recent games to the
 * repertoires they were played from (spec v1.8), so the page picks the most recent
 * game that followed one of your repertoires and compares it with the best match.
 * `?game=` and `?repertoire=` only record an explicit choice.
 */
export function GameReviewPage(): JSX.Element {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const games = useQuery({ queryKey: ['review-games'], queryFn: () => api.listGames(RECENT_GAMES) });
  const ids = useMemo(() => games.data?.items.map((game) => game.gameId) ?? [], [games.data]);
  const matchQuery = useQuery({
    queryKey: ['game-matches', ids],
    queryFn: () => api.matchGameRepertoires(ids),
    enabled: ids.length > 0,
  });
  const matches = useMemo(
    () => new Map<string, GameRepertoireMatch>((matchQuery.data ?? []).map((m) => [m.gameId, m])),
    [matchQuery.data],
  );

  function choose(next: { game?: string; repertoire?: string | null }): void {
    const updated = new URLSearchParams(params);
    if (next.game !== undefined) {
      updated.set('game', next.game);
      updated.delete('repertoire');
    }
    if (next.repertoire) updated.set('repertoire', next.repertoire);
    setParams(updated);
  }

  if (games.isPending || (ids.length > 0 && matchQuery.isPending)) {
    return <main className="page"><p role="status">Finding your recent games…</p></main>;
  }
  if (games.isError || matchQuery.isError) {
    return (
      <main className="page">
        <p role="alert">
          Could not load your games.{' '}
          <button type="button" onClick={() => { void games.refetch(); void matchQuery.refetch(); }}>Try again</button>
        </p>
      </main>
    );
  }
  const items = games.data.items;
  if (items.length === 0) {
    return (
      <main className="page game-review-empty">
        <p className="studio-eyebrow">GAME REVIEW</p>
        <h1>Review your games against your repertoire</h1>
        <p className="muted">
          Link your Chess.com or Lichess account. Your games are matched to your repertoires
          automatically, and each review opens on the moment a game left your preparation.
        </p>
        <GameLinkPrompt />
        <p><Link to="/profile#chess-accounts">Manage chess accounts</Link></p>
      </main>
    );
  }

  const target = pickReviewTarget(items, matches, {
    gameId: params.get('game'),
    repertoireId: params.get('repertoire'),
  });
  return (
    <GameReview
      key={`${target.gameId}:${target.repertoireId}`}
      gameId={target.gameId!}
      repertoireId={target.repertoireId}
      match={matches.get(target.gameId!)}
      games={items}
      matches={matches}
      onSelectGame={(game) => choose({ game })}
      onSelectRepertoire={(repertoire) => choose({ game: target.gameId!, repertoire })}
    />
  );
}
